import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useApolloClient } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Clock,
    Plus,
    Loader2,
    AlertTriangle,
    User as UserIcon,
    HelpCircle,
    Coins,
    Search,
    Trash2,
    Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { computeDnfStats } from '@/lib/dnf';
import { SortableRankedRow } from '@/components/bets/SortableRankedRow';
import { RunnerActionSheet } from '@/components/bets/RunnerActionSheet';
import { PageContent } from '@/components/layout/PageContent';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { GET_RACE_DETAILS, POTENTIAL_SCORE } from '@/graphql/queries';
import type { Gender } from '@/gql/graphql';

export interface PlaceBetData {
    rankings: Array<{ runnerId: string; position: number; tokens: number }>;
}

interface PlaceBetProps {
    raceId: string;
    gender: Gender;
    existingRankings?: Array<{ runnerId: string; position: number; tokens: number }>;
    otherGenderTokens?: number;
    onTokensChange?: (count: number) => void;
    onDataRef?: (fn: (() => PlaceBetData) | null) => void;
    onCanSubmitChange?: (canSubmit: boolean) => void;
}

// ─── Main Component ───

export function PlaceBet({ raceId, gender, existingRankings, otherGenderTokens, onTokensChange, onDataRef, onCanSubmitChange }: PlaceBetProps) {
    const { t } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const client = useApolloClient();

    const [positions, setPositions] = useState<Map<string, number>>(new Map());
    const [tokenMap, setTokenMap] = useState<Map<string, number>>(new Map());
    const [potentialScores, setPotentialScores] = useState<Map<string, number>>(new Map());
    const [selectedRunnerId, setSelectedRunnerId] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAllRunners, setShowAllRunners] = useState(false);

    const { data, loading, error } = useQuery(GET_RACE_DETAILS, { variables: { id: raceId } });

    const fetchPotentialScore = useCallback((runnerId: string, position: number) => {
        client.query({
            query: POTENTIAL_SCORE,
            variables: { raceId, runnerId, position },
            fetchPolicy: 'cache-first',
        }).then(({ data: scoreData }) => {
            const score = scoreData?.potentialScore?.score;
            if (score != null) {
                setPotentialScores(prev => { const next = new Map(prev); next.set(runnerId, score); return next; });
            }
        }).catch(() => {});
    }, [client, raceId]);

    // Init from existing rankings passed by parent
    if (!initialized && existingRankings && existingRankings.length > 0) {
        const initial = new Map<string, number>();
        const initialTokens = new Map<string, number>();
        for (const r of existingRankings) {
            initial.set(r.runnerId, r.position);
            if (r.tokens > 1) initialTokens.set(r.runnerId, r.tokens);
        }
        setPositions(initial);
        if (initialTokens.size > 0) setTokenMap(initialTokens);
        setInitialized(true);
    } else if (!initialized && existingRankings) {
        setInitialized(true);
    }

    // Fetch potential scores for initialized positions
    useEffect(() => {
        if (!initialized) return;
        for (const [rid, pos] of positions) fetchPotentialScore(rid, pos);
        // Only on first init
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized]);

    const race = data?.race;
    const allRunners = (race?.runners || []).filter(r => r.gender === gender);
    const runnerMap = new Map(allRunners.map(r => [r.id!, r]));

    // Expected rank: sort by effectiveIndex (fallback utmbIndex) desc
    const expectedRankMap = useMemo(() => {
        const sorted = [...allRunners]
            .sort((a, b) => (b.effectiveIndex || b.utmbIndex || 0) - (a.effectiveIndex || a.utmbIndex || 0));
        const map = new Map<string, number>();
        sorted.forEach((r, i) => { if (r.id) map.set(r.id, i + 1); });
        return map;
    }, [allRunners]);

    // Token budget
    const tokenBudget = race?.bettingConfig?.tokenCount ?? null;
    const tokensUsedByOtherGenders = otherGenderTokens ?? 0;
    const currentTokenCost = [...positions.keys()].reduce((sum, id) => sum + (tokenMap.get(id) ?? 1), 0);
    const totalTokensUsed = currentTokenCost + tokensUsedByOtherGenders;
    const tokensRemaining = tokenBudget != null ? tokenBudget - totalTokensUsed : null;
    const isOverBudget = tokensRemaining != null && tokensRemaining < 0;
    const noTokensLeft = tokensRemaining != null && tokensRemaining < 1;

    useEffect(() => { onTokensChange?.(currentTokenCost); }, [currentTokenCost, onTokensChange]);

    // Deadline
    const deadline = race?.bettingDeadline ? new Date(race.bettingDeadline) : null;
    const [now] = useState(() => Date.now());
    const hoursLeft = deadline ? (deadline.getTime() - now) / 3600000 : 0;
    const isExpired = deadline ? hoursLeft <= 0 : false;
    const isUrgent = hoursLeft > 0 && hoursLeft < 48;

    // Derived lists
    const rankedEntries = [...positions.entries()].filter(([, pos]) => pos > 0).sort(([, a], [, b]) => a - b);
    const dnfRunners = allRunners.filter(r => r.id && positions.get(r.id) === 0);
    const unrankedRunners = [...allRunners]
        .filter(r => r.id && !positions.has(r.id))
        .sort((a, b) => (b.effectiveIndex || 0) - (a.effectiveIndex || 0));

    const takenPositions = new Set([...positions.values()].filter(p => p > 0));
    const getNextAvailable = (): number => { let pos = 1; while (takenPositions.has(pos)) pos++; return pos; };

    // Actions
    const setRunnerPosition = (runnerId: string, position: number) => {
        setPositions(prev => { const next = new Map(prev); next.set(runnerId, position); return next; });
        fetchPotentialScore(runnerId, position);
    };
    const addToRanked = (runnerId: string) => setRunnerPosition(runnerId, getNextAvailable());
    const clearRunner = (runnerId: string) => {
        setPositions(prev => { const n = new Map(prev); n.delete(runnerId); return n; });
        setPotentialScores(prev => { const n = new Map(prev); n.delete(runnerId); return n; });
        setTokenMap(prev => { const n = new Map(prev); n.delete(runnerId); return n; });
        setSelectedRunnerId(null);
    };
    const toggleDoubleDown = (runnerId: string) => {
        setTokenMap(prev => {
            const next = new Map(prev);
            if ((next.get(runnerId) ?? 1) === 1) {
                if (tokensRemaining != null && tokensRemaining < 1) return prev;
                next.set(runnerId, 2);
            } else { next.delete(runnerId); }
            return next;
        });
    };
    const setAsDnf = (runnerId: string) => { setRunnerPosition(runnerId, 0); setSelectedRunnerId(null); };

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIdx = rankedEntries.findIndex(([id]) => id === active.id);
        const newIdx = rankedEntries.findIndex(([id]) => id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;

        const reordered = [...rankedEntries];
        const [moved] = reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, moved);

        const next = new Map(positions);
        reordered.forEach(([id], i) => {
            next.set(id, i + 1);
        });
        setTimeout(() => {
            reordered.forEach(([id], i) => fetchPotentialScore(id, i + 1));
        }, 300);
        setPositions(next);
    };

    const hasPositions = positions.size > 0;

    // Expose data to parent via onDataRef (pattern from SubmitResults)
    const positionsRef = useRef(positions);
    const tokenMapRef = useRef(tokenMap);
    useEffect(() => { positionsRef.current = positions; }, [positions]);
    useEffect(() => { tokenMapRef.current = tokenMap; }, [tokenMap]);

    useEffect(() => {
        onDataRef?.(() => ({
            rankings: [...positionsRef.current.entries()].map(([runnerId, position]) => ({
                runnerId, position, tokens: tokenMapRef.current.get(runnerId) ?? 1,
            })),
        }));
        return () => onDataRef?.(null);
    }, [onDataRef]);

    // Inform parent of submit readiness
    useEffect(() => {
        onCanSubmitChange?.(hasPositions && !isOverBudget);
    }, [hasPositions, isOverBudget, onCanSubmitChange]);

    // Loading / Error
    if (loading) return (
        <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-emerald-600" /></div>
    );
    if (error) return <PageContent width="standard" className="py-8"><ErrorBanner message={tc('error.generic')} /></PageContent>;

    const genderLabel = gender === 'MALE' ? t('gender.MALE') : t('gender.FEMALE');
    const selectedRunner = selectedRunnerId ? runnerMap.get(selectedRunnerId) : null;
    const selectedPosition = selectedRunnerId ? positions.get(selectedRunnerId) : undefined;
    const isSelectedRanked = selectedPosition != null && selectedPosition > 0;
    const isSelectedUnranked = selectedRunnerId != null && selectedPosition == null;

    // Search
    const INITIAL_DISPLAY_COUNT = 8;
    const searchTerm = searchQuery.trim().toLowerCase();
    const searchResults = searchTerm
        ? unrankedRunners.filter(r => r.name?.toLowerCase().includes(searchTerm))
        : showAllRunners ? unrankedRunners : unrankedRunners.slice(0, INITIAL_DISPLAY_COUNT);
    const hasMoreRunners = !searchTerm && !showAllRunners && unrankedRunners.length > INITIAL_DISPLAY_COUNT;

    return (
        <PageContent width="standard" className="space-y-3 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3">
                <span className={cn(
                    "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide",
                    gender === 'MALE' ? "bg-blue-50 text-blue-700" : "bg-pink-50 text-pink-700"
                )}>{genderLabel}</span>
                <span className="text-sm text-slate-400 font-medium">{allRunners.length} {tc('races:details.runners')}</span>
                <Link to="/scoring" className="ml-auto flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-indigo-500 transition-colors">
                    <HelpCircle size={14} />
                </Link>
            </div>

            {/* Token bar */}
            {tokenBudget != null && (
                <div className={cn(
                    "flex items-center gap-2 rounded-xl px-3 py-2 border",
                    isOverBudget ? "bg-red-50 border-red-200" : tokensRemaining === 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-100"
                )}>
                    <Coins size={16} className={cn(isOverBudget ? "text-red-500" : tokensRemaining === 0 ? "text-emerald-600" : "text-amber-500")} />
                    <span className="text-xs font-bold text-slate-700">{t('tokens.used', { used: totalTokensUsed, total: tokenBudget })}</span>
                    <span className={cn("text-xs font-black ml-auto", isOverBudget ? "text-red-600" : tokensRemaining === 0 ? "text-emerald-600" : "text-amber-600")}>
                        {isOverBudget ? t('tokens.exceeded') : tokensRemaining === 0 ? t('tokens.allPlaced') : t('tokens.remaining', { count: tokensRemaining ?? 0 })}
                    </span>
                </div>
            )}

            {/* Alerts */}
            {isExpired && <ErrorBanner message={t('deadlineExpired')} />}
            {isUrgent && !isExpired && (
                <div className="flex items-center gap-3 rounded-xl bg-amber-50 p-3 border border-amber-100">
                    <Clock size={16} className="text-amber-600 flex-shrink-0" />
                    <p className="text-xs font-bold text-amber-900">{tc('races:details.urgentMessage', { hours: Math.floor(hoursLeft) })}</p>
                </div>
            )}

            {/* Empty */}
            {allRunners.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <UserIcon size={32} className="mb-2 opacity-20" />
                    <p className="text-sm font-medium">{t('noRunners')}</p>
                </div>
            )}

            {/* ══════ RANKED LIST (drag & drop) ══════ */}
            {rankedEntries.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={rankedEntries.map(([id]) => id)} strategy={verticalListSortingStrategy}>
                        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                            {rankedEntries.map(([runnerId, position]) => {
                                const runner = runnerMap.get(runnerId);
                                if (!runner) return null;
                                const isDoubled = (tokenMap.get(runnerId) ?? 1) > 1;
                                const score = potentialScores.get(runnerId);
                                return (
                                    <SortableRankedRow
                                        key={runnerId}
                                        id={runnerId}
                                        position={position}
                                        name={runner.name ?? ''}
                                        onTap={() => setSelectedRunnerId(runnerId)}
                                        badges={<>
                                            {isDoubled && <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-700 flex-shrink-0">x2</span>}
                                            {score != null && <span className="text-[10px] font-bold text-indigo-500 flex-shrink-0"><Zap size={10} className="inline -mt-0.5" /> +{Math.round(score)}</span>}
                                        </>}
                                    />
                                );
                            })}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* ══════ DNF LIST ══════ */}
            {dnfRunners.length > 0 && (
                <div>
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1 mb-1 flex items-center gap-1">
                        <AlertTriangle size={10} />DNF
                    </h3>
                    <div className="rounded-2xl bg-white border border-amber-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                        {dnfRunners.map(runner => {
                            const isDoubled = (tokenMap.get(runner.id!) ?? 1) > 1;
                            return (
                                <button
                                    key={runner.id}
                                    onClick={() => setSelectedRunnerId(runner.id!)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-amber-50/50 transition-colors"
                                >
                                    <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-600 text-[10px] font-black flex items-center justify-center flex-shrink-0">
                                        DNF
                                    </span>
                                    <span className="flex-1 min-w-0 text-sm font-semibold text-slate-700 truncate">{runner.name}</span>
                                    {isDoubled && (
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-700 flex-shrink-0">x2</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ══════ ADD RUNNER (inline search) ══════ */}
            {unrankedRunners.length > 0 && !isExpired && (
                <div>
                    <div className="relative mb-2">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('section.unranked')}
                            className="w-full h-10 pl-9 pr-3 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                    </div>
                    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                        {searchResults.map(runner => {
                            const dnfStats = computeDnfStats(runner.dnfRatio, runner.formatStats);
                            return (
                            <div key={runner.id} className="flex items-center gap-2 px-3 py-2">
                                <button
                                    onClick={() => setSelectedRunnerId(runner.id!)}
                                    className="flex-1 min-w-0 text-left hover:bg-slate-50 -mx-1 px-1 py-0.5 rounded-lg transition-colors"
                                >
                                    <span className="text-sm font-medium text-slate-700 truncate block">{runner.name}</span>
                                    <span className="text-[11px] text-slate-400">UTMB {Math.round(runner.utmbIndex || 0)}{dnfStats != null ? ` · DNF ${dnfStats.dnfCount}/${dnfStats.totalRaces}` : ''}</span>
                                </button>
                                <button
                                    onClick={() => addToRanked(runner.id!)}
                                    disabled={noTokensLeft}
                                    className={cn(
                                        "w-8 h-8 flex items-center justify-center rounded-lg transition-all active:scale-90 flex-shrink-0",
                                        noTokensLeft ? "bg-slate-100 text-slate-300 cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                                    )}
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            );
                        })}
                        {searchResults.length === 0 && searchTerm && (
                            <div className="px-3 py-4 text-center text-xs text-slate-400">{tc('races:details.filter.noResults')}</div>
                        )}
                    </div>
                    {hasMoreRunners && (
                        <button
                            onClick={() => setShowAllRunners(true)}
                            className="w-full mt-2 py-2 text-xs font-bold text-slate-500 hover:text-emerald-600 transition-colors"
                        >
                            {t('section.showMore', { count: unrankedRunners.length - INITIAL_DISPLAY_COUNT })}
                        </button>
                    )}
                </div>
            )}

            {/* ══════ ACTION SHEET ══════ */}
            {selectedRunner && selectedRunnerId && (
                <RunnerActionSheet
                    runner={selectedRunner}
                    raceFormat={race?.raceFormat}
                    expectedRank={expectedRankMap.get(selectedRunnerId)}
                    potentialScore={potentialScores.get(selectedRunnerId)}
                    onClose={() => setSelectedRunnerId(null)}
                >
                            {/* Unranked: primary CTA */}
                            {isSelectedUnranked && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { addToRanked(selectedRunnerId); setSelectedRunnerId(null); }}
                                        disabled={noTokensLeft}
                                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 transition-all disabled:opacity-40 font-bold text-sm text-white shadow-sm"
                                    >
                                        <Plus size={16} /> Classer
                                    </button>
                                    <button
                                        onClick={() => setAsDnf(selectedRunnerId)}
                                        disabled={noTokensLeft}
                                        className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-amber-50 transition-all disabled:opacity-40 font-bold text-sm text-slate-600"
                                    >
                                        DNF
                                    </button>
                                </div>
                            )}

                            {/* Ranked: position centered + actions below */}
                            {isSelectedRanked && (
                                <>
                                    <div className="flex items-center justify-center gap-3">
                                        <span className="text-sm font-medium text-slate-500">Position</span>
                                        <input
                                            type="number"
                                            min={1}
                                            value={selectedPosition}
                                            onChange={(e) => { const v = parseInt(e.target.value, 10); if (v > 0) setRunnerPosition(selectedRunnerId, v); }}
                                            className="w-16 h-11 text-center text-xl font-black text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                    </div>
                                    <div className="flex items-center justify-center gap-2">
                                        {tokenBudget != null && (
                                            <DoubleDownButton
                                                isActive={(tokenMap.get(selectedRunnerId) ?? 1) > 1}
                                                disabled={noTokensLeft}
                                                onToggle={() => toggleDoubleDown(selectedRunnerId)}
                                            />
                                        )}
                                        <button
                                            onClick={() => setAsDnf(selectedRunnerId)}
                                            className="h-9 px-4 rounded-xl text-xs font-bold bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-amber-600 flex items-center gap-1.5 transition-all"
                                        >
                                            <AlertTriangle size={12} /> DNF
                                        </button>
                                        <button
                                            onClick={() => clearRunner(selectedRunnerId)}
                                            className="h-9 px-3 rounded-xl text-xs font-bold text-red-400 bg-slate-100 hover:bg-red-50 hover:text-red-500 flex items-center gap-1.5 transition-all"
                                        >
                                            <Trash2 size={12} /> Retirer
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* DNF runner — same layout as ranked: position input + actions */}
                            {selectedPosition === 0 && (
                                <>
                                    <div className="flex items-center justify-center gap-3">
                                        <span className="text-sm font-medium text-slate-500">Position</span>
                                        <input
                                            type="number"
                                            min={1}
                                            placeholder="—"
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value, 10);
                                                if (v > 0) setRunnerPosition(selectedRunnerId, v);
                                            }}
                                            className="w-16 h-11 text-center text-xl font-black text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                    </div>
                                    <div className="flex items-center justify-center gap-2">
                                        {tokenBudget != null && (
                                            <DoubleDownButton
                                                isActive={(tokenMap.get(selectedRunnerId) ?? 1) > 1}
                                                disabled={noTokensLeft}
                                                onToggle={() => toggleDoubleDown(selectedRunnerId)}
                                            />
                                        )}
                                        <button
                                            onClick={() => clearRunner(selectedRunnerId)}
                                            className="h-9 px-3 rounded-xl text-xs font-bold text-red-400 bg-slate-100 hover:bg-red-50 hover:text-red-500 flex items-center gap-1.5 transition-all"
                                        >
                                            <Trash2 size={12} /> Retirer
                                        </button>
                                    </div>
                                </>
                            )}
                </RunnerActionSheet>
            )}
        </PageContent>
    );
}

// ─── Double Down Button ───

function DoubleDownButton({ isActive, disabled, onToggle }: {
    isActive: boolean;
    disabled: boolean;
    onToggle: () => void;
}) {
    if (disabled && !isActive) return null;

    return (
        <button
            onClick={onToggle}
            className={cn(
                "h-9 px-4 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all",
                isActive
                    ? "bg-amber-100 text-amber-700 ring-1 ring-amber-300"
                    : "bg-slate-100 text-slate-400 hover:bg-amber-50 hover:text-amber-600"
            )}
        >
            <Zap size={14} /> x2
        </button>
    );
}
